/**
 * SetupRouteGuard (Phase 44.3).
 *
 * Gates the main app routes on tenant status + caller role:
 *
 *   status == "setup_review"  +  role == "tenant-admin"   → redirect to /setup
 *   status == "setup_review"  +  role == "member"         → redirect to /setup-pending
 *   status == "active"        +  path starts with /setup  → redirect to /
 *   super-admin anywhere                                  → pass through
 *   otherwise                                             → pass through
 *
 * Reads tenant status from the existing useStore slice (loaded by the
 * setup-incomplete banner on every authenticated render). If the slice
 * hasn't loaded yet, we pass through without redirecting — the banner
 * will resolve the state shortly and a subsequent render will redirect
 * if needed. This avoids a flash-of-wrong-redirect during initial load.
 *
 * Super-admin routes (/admin/*) are exempt — super-admins operate
 * cross-tenant and shouldn't be yanked out of an admin view just
 * because the selected tenant is in setup_review.
 */

import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useStore } from "../store/useStore.js";

const SETUP_PATH = "/setup";
const MEMBER_PLACEHOLDER_PATH = "/setup-pending";

function isExemptPath(pathname) {
  // Admin panel is cross-tenant; super-admin operations don't depend on
  // the viewed tenant's status. Also exempt: /setup itself, the pending
  // placeholder, and auth callback.
  return (
    pathname.startsWith("/admin") ||
    pathname.startsWith(SETUP_PATH) ||
    pathname === MEMBER_PLACEHOLDER_PATH ||
    pathname === "/callback" ||
    pathname === "/login" ||
    pathname.startsWith("/share/")
  );
}

export default function SetupRouteGuard({ children }) {
  const location = useLocation();
  const tenantStatus = useStore((s) => s.tenantStatus);
  const tenantStatusFetchedAt = useStore((s) => s.tenantStatusFetchedAt);
  const loadTenantStatus = useStore((s) => s.loadTenantStatus);

  // Kick off a status load on mount only. The store's 60s staleness TTL
  // (Phase 43.4) coalesces repeat calls across both this guard and the
  // banner, and the banner also refreshes on window focus. Re-fetching
  // on every pathname change caused thrash without adding freshness.
  useEffect(() => {
    loadTenantStatus();
  }, [loadTenantStatus]);

  // Exempt paths don't need a status resolution to render — pass through
  // while we wait (or unconditionally).
  if (isExemptPath(location.pathname)) return children;

  // First-load gate: while the tenantStatus slice is still resolving,
  // hold protected routes behind a minimal spinner rather than rendering
  // the target page. This prevents a deep-linked tenant-admin on a
  // setup_review tenant from seeing the protected page (and its child
  // components' failed API calls) flash for several hundred ms before
  // the redirect fires. Once the slice resolves at least once
  // (fetchedAt > 0), we trust the current value and pass through on
  // null (post-load error state; exempt paths handle auth redirect).
  if (!tenantStatus && tenantStatusFetchedAt === 0) {
    return <div className="setup-guard-loading" aria-label="Loading" />;
  }
  if (!tenantStatus) return children;

  if (tenantStatus.role === "super-admin") return children;

  if (tenantStatus.status === "setup_review") {
    if (tenantStatus.role === "tenant-admin") {
      return <Navigate to={SETUP_PATH} replace />;
    }
    return <Navigate to={MEMBER_PLACEHOLDER_PATH} replace />;
  }

  if (tenantStatus.status === "provisioning") {
    // Bootstrap is still running — nobody can edit yet. Both admins and
    // members land on the pending placeholder; it auto-refreshes via the
    // banner's focus-poll and they'll get redirected to /setup or / once
    // the bootstrap SM flips status.
    return <Navigate to={MEMBER_PLACEHOLDER_PATH} replace />;
  }

  if (tenantStatus.status === "active" && location.pathname.startsWith(SETUP_PATH)) {
    return <Navigate to="/" replace />;
  }

  return children;
}
